/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { persist } from "mobx-persist";
import { Permission } from "models/graphql";
import { post } from "utils/Api";
import { StoreBase } from "./StoreBase";
import deviceConfigSubMenus, {
  DeviceConfigSubMenu,
} from "./types/DeviceConfigSubMenu";
import deviceStatusPanelSubMenus, {
  DeviceStatusPanelSubMenu,
} from "./types/DeviceStatusPanelSubMenu";

export interface LoginArgs {
  email: string;
  access: Access;
  jwtToken: string;
}

interface Access {
  id: number;
  type: string;
  permissions: string[];
}

export class AuthStore extends StoreBase {
  constructor() {
    super();
    makeObservable(this);

    reaction(
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => [this.email],
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
    );
  }

  @observable @persist public email?: string;
  @observable @persist public sessionExpired = false;
  @observable @persist private role?: string;
  @observable @persist("list") private permissions: string[] = [];
  @observable @persist public jwtToken = "";
  @observable public isFailedLogIn = false;

  @computed get isLoggedIn(): boolean {
    return this.email != null;
  }

  @computed get canManageAggregators(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.aggregatorManagement,
    );
  }

  @computed get canViewAggregatorAssignedDevices(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.aggregatorAssignedDevicesView,
    );
  }

  @computed get canViewDsrMenu(): boolean {
    const permission = this.permissions.some(
      (elem) => elem === Permission.aggregatorView,
    );

    return permission;
  }

  @computed get canExportCustomersToCsv(): boolean {
    if (process.env.ENV_NAME !== "prod") {
      return true;
    }

    if (process.env.ALLOWED_TO_EXPORT_CUSTOMER_DATA && this.email) {
      const emails = process.env.ALLOWED_TO_EXPORT_CUSTOMER_DATA.split(",");

      return emails.includes(this.email);
    }

    return false;
  }

  @computed get canViewUsers(): boolean {
    return this.permissions.some((elem) => elem === Permission.userView);
  }

  @computed get canManageUsers(): boolean {
    return this.permissions.some((elem) => elem === Permission.userManagement);
  }

  @computed get canManageInstallers(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.manageInstallers,
    );
  }

  @computed get canManageServers(): boolean {
    return this.permissions.some((elem) => elem === Permission.manageServers);
  }

  @computed get canViewDashboard(): boolean {
    return this.permissions.some((elem) => elem === Permission.dashboardView);
  }

  @computed get visibleSubMenus(): DeviceConfigSubMenu[] {
    return deviceConfigSubMenus.filter((subMenu) =>
      this.permissions.some((elem) => elem === subMenu.requiredPermission),
    );
  }

  @computed get visibleStatusPanelSubMenus(): DeviceStatusPanelSubMenu[] {
    return deviceStatusPanelSubMenus.filter((subMenu) =>
      this.permissions.some((elem) => elem === subMenu.requiredPermission),
    );
  }

  @computed get canDeregisterHub(): boolean {
    return this.permissions.some((elem) => elem === Permission.hubDeregister);
  }

  @computed get canViewHubDetails(): boolean {
    return this.permissions.some((elem) => elem === Permission.hubDetailsView);
  }

  @computed get canViewHubsMenu(): boolean {
    return this.permissions.some((elem) => elem === Permission.hubView);
  }

  @computed get canDeviceDelete(): boolean {
    return this.permissions.some((elem) => elem === Permission.deviceDelete);
  }

  @computed get canViewCustomers(): boolean {
    return this.permissions.some((elem) => elem === Permission.customerView);
  }

  @computed get canViewFirmware(): boolean {
    return this.permissions.some((elem) => elem === Permission.firmwareView);
  }

  @computed get canManageFirmware(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.firmwareManagement,
    );
  }

  @computed get canManageFirmwareRelease(): boolean {
    return this.permissions.some((elem) => elem === Permission.firmwareRelease);
  }

  @computed get canManageFirmwarePrerelease(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.firmwarePrerelease,
    );
  }

  @computed get canManageReleaseChannelDevices(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.deviceSetFirmware,
    );
  }

  @computed get canCustomerLiveView(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.customerLiveView,
    );
  }

  @computed get canEditCustomers(): boolean {
    return this.permissions.some((elem) => elem === Permission.customerEdit);
  }

  @computed get canDeleteCustomers(): boolean {
    return this.permissions.some((elem) => elem === Permission.deleteCustomer);
  }

  @computed get canDeleteMyAccountDevices(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.deleteMyAccountDevices,
    );
  }

  @computed get canDeviceBlock(): boolean {
    return this.permissions.some((elem) => elem === Permission.deviceBlock);
  }

  @computed get canViewDeviceConfig(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.deviceConfigView,
    );
  }

  @computed get canEditDeviceConfig(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.deviceConfigEdit,
    );
  }

  @computed get canDownloadBootloader(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.downloadBootloader,
    );
  }

  @computed get canForceReboot(): boolean {
    return this.permissions.some((elem) => elem === Permission.forceReboot);
  }

  @computed get canForceBackgroundDownload(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.forceBackgroundDownload,
    );
  }

  @computed get canInstallFirmware(): boolean {
    return this.permissions.some((elem) => elem === Permission.forceInstallApp);
  }

  @computed get canViewIntegrators(): boolean {
    return this.permissions.some((elem) => elem === Permission.integratorView);
  }

  @computed get canManageIntegrators(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.integratorManagement,
    );
  }

  @computed get canInstallBootloader(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.forceInstallBootloader,
    );
  }

  @computed get canClearInitialCommands(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.deleteInitialCommands,
    );
  }

  @computed get canViewAccessManagement(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.deviceAccessGroupsView,
    );
  }

  @computed get canEditAccessManagement(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.deviceAccessGroupsEdit,
    );
  }

  @computed get canEditGoodWillWarranty(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.goodwillWarrantyEdit,
    );
  }

  @computed get canEditDeviceRegistrationDate(): boolean {
    return this.permissions.some(
      (elem) => elem === Permission.registrationDateEdit,
    );
  }

  @computed get canEditLibbiComponents(): boolean {
    if (!this.email) return false;

    /**
     * The users who can access this feature are hard coded rather than using
     * the user roles feature because support requested this and we didn't tell
     * them no, or something. This is probably a really stupid thing to do.
     */
    const usersAllowedToEditLibbiComponents = [
      "valentin.acatrinei@ddroidd.com",
      "madalina.rusu@ddroidd.com",
      "valentina.josu@ddroidd.com",
      "arany.mak@ddroidd.com",
      "ferenc.pal@ddroidd.com",
      "calin.lurci@ddroidd.com",
      "sergiu.petrut@ddroidd.com",
      "gabi.plesa@ddroidd.com",
      "vlad.selegean@ddroidd.com",
      "jack.dean@myenergi.com",
      "antonio.luis@myenergi.com",
      "chris.davis@myenergi.com",
      "darren.konradsen@myenergi.com",
      "peter.lawless@myenergi.com",
    ];

    return usersAllowedToEditLibbiComponents.includes(this.email);
  }

  @computed get canViewOcppEnabledDevices(): boolean {
    return this.permissions.some((elem) => elem === Permission.ocppDeviceView);
  }

  @action.bound
  public login({ email, access, jwtToken }: LoginArgs): void {
    this.email = email;
    this.role = access.type;
    this.permissions = access.permissions;
    this.sessionExpired = false;
    this.jwtToken = jwtToken;
  }

  @action.bound
  public logout(): void {
    this.email = undefined;
    this.role = undefined;
    this.permissions = [];
    post("auth/logout", {});
  }

  @action.bound
  public setSessionExpired(value: boolean) {
    this.sessionExpired = value;
  }

  @action.bound
  public setIsFailedLogin(value: boolean) {
    this.isFailedLogIn = value;
  }
}

export default AuthStore;
